import { useRef, useCallback, useState } from "react";
import type { TextDrawing, Point } from "./types";

interface TextOverlayProps {
    drawing: TextDrawing;
    canvasScale: number;
    viewScale: number;
    isSelected: boolean;
    interactable: boolean;
    boundsRef: React.RefObject<HTMLDivElement | null>;
    onSelect: (id: string) => void;
    onDragEnd: (id: string, newPosition: Point) => void;
    onTapToEdit: (id: string) => void;
    onActivate?: (id: string) => void;
}

export function TextOverlay({
    drawing,
    canvasScale,
    viewScale,
    isSelected,
    interactable,
    boundsRef,
    onSelect,
    onDragEnd,
    onTapToEdit,
    onActivate,
}: TextOverlayProps) {
    // canvasScale is now baseScale only (no viewScale). Since the text overlay sits
    // inside a CSS-transformed wrapper that scales by viewScale, drag deltas in screen
    // pixels must be converted using baseScale/viewScale for 1:1 finger tracking.
    const dragScale = canvasScale / viewScale;
    // Local drag offset in canvas-space — applied during drag for smooth visual feedback
    // without mutating the parent's history state
    const [dragOffset, setDragOffset] = useState<Point | null>(null);

    const dragRef = useRef<{
        startClientX: number;
        startClientY: number;
        startCanvasX: number;
        startCanvasY: number;
        totalMovement: number;
        pointerId: number;
    } | null>(null);

    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            e.stopPropagation();
            e.preventDefault();

            // Second finger while a drag is active — this is the start of a pinch gesture.
            // Cancel the drag immediately and release pointer capture so the container's
            // native touch handler can take over without interference.
            if (dragRef.current) {
                const el = e.currentTarget as HTMLElement;
                try { el.releasePointerCapture(dragRef.current.pointerId); } catch { /* ignore */ }
                setDragOffset(null);
                dragRef.current = null;
                // Don't capture this second pointer — let the pinch flow to the container.
                return;
            }

            if (interactable) {
                onSelect(drawing.id);
            }

            dragRef.current = {
                startClientX: e.clientX,
                startClientY: e.clientY,
                startCanvasX: drawing.position.x,
                startCanvasY: drawing.position.y,
                totalMovement: 0,
                pointerId: e.pointerId,
            };

            (e.target as HTMLElement).setPointerCapture(e.pointerId);
        },
        [interactable, drawing.id, drawing.position.x, drawing.position.y, onSelect]
    );

    const isPointerInBounds = useCallback(
        (clientX: number, clientY: number): boolean => {
            const bounds = boundsRef.current;
            if (!bounds) return true;
            const rect = bounds.getBoundingClientRect();
            return (
                clientX >= rect.left &&
                clientX <= rect.right &&
                clientY >= rect.top &&
                clientY <= rect.bottom
            );
        },
        [boundsRef]
    );

    const commitDrag = useCallback(
        (e: React.PointerEvent) => {
            if (!dragRef.current) return;
            if (interactable && dragRef.current.totalMovement >= 5) {
                const dx = e.clientX - dragRef.current.startClientX;
                const dy = e.clientY - dragRef.current.startClientY;
                const newPosition: Point = {
                    x: dragRef.current.startCanvasX + dx * dragScale,
                    y: dragRef.current.startCanvasY + dy * dragScale,
                };
                onDragEnd(drawing.id, newPosition);
            }
            setDragOffset(null);
            dragRef.current = null;
            (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        },
        [interactable, dragScale, drawing.id, onDragEnd]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (!dragRef.current) return;
            e.stopPropagation();

            // End drag if pointer leaves the image bounds
            if (!isPointerInBounds(e.clientX, e.clientY)) {
                commitDrag(e);
                return;
            }

            const dx = e.clientX - dragRef.current.startClientX;
            const dy = e.clientY - dragRef.current.startClientY;
            dragRef.current.totalMovement += Math.abs(dx) + Math.abs(dy);

            if (interactable) {
                // Update local offset for visual feedback (no parent state mutation)
                setDragOffset({
                    x: dx * dragScale,
                    y: dy * dragScale,
                });
            }
        },
        [interactable, dragScale, isPointerInBounds, commitDrag]
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (!dragRef.current) return;
            e.stopPropagation();

            if (!interactable) {
                if (dragRef.current.totalMovement < 5) {
                    onActivate?.(drawing.id);
                }
                dragRef.current = null;
                setDragOffset(null);
                return;
            }

            if (dragRef.current.totalMovement < 5) {
                if (isSelected) {
                    onTapToEdit(drawing.id);
                }
            } else {
                // Drag end — commit position
                const dx = e.clientX - dragRef.current.startClientX;
                const dy = e.clientY - dragRef.current.startClientY;
                const newPosition: Point = {
                    x: dragRef.current.startCanvasX + dx * dragScale,
                    y: dragRef.current.startCanvasY + dy * dragScale,
                };
                onDragEnd(drawing.id, newPosition);
            }

            setDragOffset(null);
            dragRef.current = null;
        },
        [interactable, dragScale, drawing.id, isSelected, onActivate, onDragEnd, onTapToEdit]
    );

    // Apply local drag offset for smooth visual feedback during drag
    const posX = drawing.position.x + (dragOffset?.x ?? 0);
    const posY = drawing.position.y + (dragOffset?.y ?? 0);
    const displayX = posX / canvasScale;
    const displayY = posY / canvasScale;
    const displayFontSize = drawing.fontSize / canvasScale;
    const displayMaxWidth = drawing.maxWidth / canvasScale;

    return (
        <div
            data-text-overlay="true"
            data-text-overlay-id={drawing.id}
            style={{
                position: "absolute",
                left: displayX,
                top: displayY,
                width: displayMaxWidth,
                display: "flex",
                justifyContent: "center",
                userSelect: "none",
                WebkitUserSelect: "none",
                pointerEvents: interactable ? "auto" : "none",
                touchAction: "none",
                cursor: interactable ? "grab" : "default",
                outline: "none",
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <div
                style={{
                    maxWidth: displayMaxWidth,
                    fontSize: displayFontSize,
                    fontWeight: "bold",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    color: drawing.color,
                    background: "rgba(255, 255, 255, 0.9)",
                    borderRadius: displayFontSize * 0.6,
                    padding: `${displayFontSize * 0.1}px ${displayFontSize * 0.2}px`,
                    lineHeight: 1.4,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    textAlign: "center",
                }}
            >
                {drawing.text}
            </div>
        </div>
    );
}
